import type { Json } from '@/integrations/supabase/types';
import type { Block } from '@/types/database';

// Advisory-only checks, run client-side over already-loaded Studio blocks
// at Publish time. Nothing here is a security boundary — the blocking
// youth-PII check lives server-side (see
// supabase/migrations/20260714163000_publish_pii_scan.sql). These exist to
// nudge toward good practice (no exposed personal contact info, images
// with alt text), not to enforce an invariant, so they're deliberately
// simple regexes rather than a comprehensive NLP pass — a known
// false-positive (e.g. a meeting time like "555-1234" reading as a phone
// number) just costs an extra glance the leader can dismiss.

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const PHONE_RE = /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/;
const ADDRESS_RE =
  /\b\d{1,5}\s+([A-Za-z0-9.]+\s){1,4}(Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Way|Place|Pl)\b/i;

export type AdvisoryIssueType = 'email' | 'phone' | 'address' | 'missing-alt-text';

export interface AdvisoryIssue {
  type: AdvisoryIssueType;
  message: string;
}

// Recursively walks a block's jsonb content and collects every string leaf
// into one searchable string — mirrors the SQL side's
// flatten_jsonb_strings() so both languages apply the same conceptual
// check, even though they're independent implementations (advisory drift
// between them has no security consequence, so sharing code across the
// network boundary isn't worth the coupling).
export function flattenContentStrings(content: Json): string {
  const parts: string[] = [];
  function walk(value: Json): void {
    if (typeof value === 'string') {
      parts.push(value);
    } else if (Array.isArray(value)) {
      value.forEach(walk);
    } else if (value && typeof value === 'object') {
      Object.values(value).forEach(walk);
    }
  }
  walk(content);
  return parts.join(' ');
}

export function matchEmails(text: string): boolean {
  return EMAIL_RE.test(text);
}

export function matchPhones(text: string): boolean {
  return PHONE_RE.test(text);
}

export function matchAddresses(text: string): boolean {
  return ADDRESS_RE.test(text);
}

// image blocks flag a genuinely missing alt field; hero blocks have no alt
// field at all today, so any hero image is flagged with a distinct message
// rather than silently treated as compliant.
function checkMissingAltText(block: Block): AdvisoryIssue | null {
  const content = block.content as Record<string, unknown> | null;
  if (!content) return null;

  if (block.block_type === 'image' && content.imageUrl && !content.alt) {
    return { type: 'missing-alt-text', message: 'This image has no alt text — add a short description for screen readers.' };
  }
  if (block.block_type === 'hero' && content.imageUrl) {
    return { type: 'missing-alt-text', message: 'Hero images don’t support alt text yet — this image is invisible to screen readers.' };
  }
  return null;
}

export function scanBlockAdvisory(block: Block): AdvisoryIssue[] {
  const issues: AdvisoryIssue[] = [];
  const text = flattenContentStrings(block.content);

  if (matchEmails(text)) {
    issues.push({ type: 'email', message: 'This block contains what looks like an email address — consider a masked contact form instead.' });
  }
  if (matchPhones(text)) {
    issues.push({ type: 'phone', message: 'This block contains what looks like a phone number — consider a masked contact form instead.' });
  }
  if (matchAddresses(text)) {
    issues.push({ type: 'address', message: 'This block contains what looks like a street address — double-check it isn’t a home address.' });
  }
  const altIssue = checkMissingAltText(block);
  if (altIssue) issues.push(altIssue);

  return issues;
}
